import _ from 'lodash';
import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { selectDialogState } from 'app/dialog/features/dialog.selectors';
import { DialogService } from 'app/dialog/services/dialog.service';
import { selectCampaignList } from 'app/campaign/features/campaign.selectors';
import { first, map } from 'rxjs/operators';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { CAMPAIGN_TYPE } from 'app/virtual-event/virtual-event.enums';
import { TicketComboSchema } from 'app/campaign/structure/ticketing';
import { CampaignService } from 'app/campaign/services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MdbFormsModule } from 'mdb-angular-ui-kit/forms';
import { MdbSelectModule } from 'mdb-angular-ui-kit/select';
import { MdbTooltipModule } from 'mdb-angular-ui-kit/tooltip';
import { MdbpInputComponent } from '../../mdb-input/mdb-input.component';
import { MdbValidationModule } from 'mdb-angular-ui-kit/validation';
import { NgxCurrencyDirective } from 'ngx-currency';
import { MdbSwitchComponent } from '../../../../../../projects/donor/src/app/components/controls/mdb-switch/mdb-switch.component';

@Component({
  standalone:true,
  imports:[CommonModule,FormsModule, ReactiveFormsModule,MdbFormsModule,MdbSelectModule,
      MdbTooltipModule,MdbpInputComponent,MdbValidationModule,NgxCurrencyDirective,MdbSwitchComponent],
  selector: 'app-ticket-combo-modal',
  templateUrl: './ticket-combo-modal.component.html',
  styleUrls: ['./ticket-combo-modal.component.scss']
})
export class TicketComboComponent implements OnInit, OnDestroy {
  destroyRef = inject(DestroyRef)
  public dialog$: Observable<any> = this.store.pipe(select(selectDialogState));

  public campaigns$ = this.store.pipe(
    select(selectCampaignList),
    map(list => this.getFilteredCampaignList(list, CAMPAIGN_TYPE.TICKETING, null))
  );

  private action = null;

  public campaigns = [];
  public options = [];
  public ticketCombo = []

  public campaign = null;
  public option = null;
  public optionId = null;

  public form: UntypedFormGroup = null;

  public takeUntilSubject: Subject<any> = new Subject();
  
  constructor(
    private store: Store,
    private formBuilder: UntypedFormBuilder,
    private campaignService: CampaignService,
    private dialogService: DialogService,
  ) {
    this.form = this.formBuilder.group({});
    this.campaignService.generateForm(this.form, TicketComboSchema);
  }

  ngOnInit(): void {
    this.campaigns$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(list => this.campaigns = list);

    this.form.get('campaignId').valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((campaignId) => {
      const campaign = _.find(this.campaigns, campaign => campaign.id === campaignId) || null;
      if(campaign === null) {
        return;
      }

      this.campaign = campaign;

      this.form.get('campaignIntId').setValue(campaign.intid);

      const presetOptions = campaign.campaignTypeData.presetOptions || [];
      const activePresetOptions = _.filter(presetOptions, option => option.status === 0);

      this.options = _.filter(activePresetOptions, presetOption => {
        return this.isNotComboTicket(presetOption);
      });
      this.option = null;

      this.form.get('optionId').setValue(null, {
        emitEvent: false
      });
    });

    this.dialog$.pipe(
      first(),
      map(({ initialState }) => _.cloneDeep(initialState)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((initialState) => {
      this.action =  _.get(initialState, 'action', 'add');

      const option = _.get(initialState, 'option', null);

      this.optionId = _.get(initialState, 'optionId', null);
      this.ticketCombo = _.get(initialState, 'ticketCombo', []);

      if(option) {
        this.campaignService.initializeForm(this.form, option, TicketComboSchema);
      }
    });
  }

  ngOnDestroy(): void {
    this.takeUntilSubject.next(null);
    this.takeUntilSubject.complete();

    this.dialogService.closeDialogSubject.next(null);
  }

  isNotComboTicket(option) {
    const { ticketCombo } = this;
    const { length: ticketComboLength } = option.ticketCombo || [];
    if(ticketComboLength > 0) {
      return false;
    }
    if(option.id === this.optionId) {
      return false;
    }
    if(this.action === 'edit') {
      return true;
    }
    for(let i = 0; i < ticketCombo.length; i++) {
      const { optionId } = ticketCombo[i];
      if(optionId === option.id) {
        return false;
      }
    }
    return true;
  }

  getFilteredCampaignList(campaigns = [], type = null, status = null) {
    const campaignsFilteredByType = _.isNull(type) ? campaigns: _.filter(campaigns, campaign => {
      return _.isArray(type) ?
        _.indexOf(type, campaign.type) >= 0:
        _.isEqual(campaign.type, type);
    }) || [];
    const campaignsFilteredByStatus = _.isNull(status) ? campaignsFilteredByType: _.filter(campaignsFilteredByType, campaign => {
      return _.isArray(status) ?
        _.indexOf(status, campaign.status) >= 0:
        _.isEqual(campaign.status, status);
    }) || [];
    return _.sortBy(campaignsFilteredByStatus, [function(campaign) { return -campaign.updated; }]);
  }

  close(dialog, value) {
    const { initialState: { id, action } } = dialog;
    this.dialogService.closeDialogSubject.next({ id, action, name: 'ticketCombo', value });
  }
  
  confirm(dialog, value) {
    if(this.form.invalid) {
      return this.form.markAllAsTouched();
    }
    return this.close(dialog, value);
  }
}
